import {Injectable, signal} from '@angular/core';
import {BehaviorSubject, lastValueFrom, Observable, startWith} from 'rxjs';
import {environment} from '../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {Event, RecurringEvent} from '../../models/event';
import {RealtimeService} from './realtime.service';

@Injectable({
  providedIn: 'root'
})
export class EventService {
  private url = environment.backendUrl;
  eventList = signal<Event[]>([]);

  private eventIdSubject = new BehaviorSubject<string | null>(null);
  eventId$: Observable<string | null> = this.eventIdSubject.asObservable();

  private currentStartDate?: number;
  private currentEndDate?: number;

  openEvent(eventId: string) {
    this.eventIdSubject.next(eventId);
  }

  resetEvent() {
    this.eventIdSubject.next(null);
  }


  constructor(
    private realtime: RealtimeService,
    private http: HttpClient,
  ) {
    this.realtime.events.pipe(startWith(null))
      .subscribe(() => {
        if (this.currentStartDate != null && this.currentEndDate != null) {
          this.getByRange(this.currentStartDate, this.currentEndDate);
        }
      })
  }

  async getByRange(startDate: number, endDate: number) {
    this.currentStartDate = startDate;
    this.currentEndDate = endDate;
    const res = await lastValueFrom(
      this.http.get<{ data: Event[], pagination: { total: number } }>(
        this.url + `/v2/events?startDate=${startDate}&endDate=${endDate}`
      )
    );
    this.eventList.set(res.data);
    return res.data;
  }

  async searchEvents(query: string, limit = 10): Promise<Event[]> {
    const res = await lastValueFrom(
      this.http.get<{ data: Event[], pagination: { total: number } }>(
        this.url + `/v2/events?search=${encodeURIComponent(query)}&limit=${limit}`
      )
    );
    return res.data;
  }

  async getById(id: string): Promise<Event | null> {
    try {
      return await lastValueFrom(
        this.http.get<Event>(this.url + `/v2/events/${id}`)
      );
    } catch {
      return null;
    }
  }

  async create(data: Event) {
    return lastValueFrom(this.http.post<Event[]>(this.url + `/events`, data));
  }

  async update(id:string, data: Event) {
    return lastValueFrom(this.http.put<Event[]>(this.url + `/events/${id}`, data));
  }

  async delete(id:string) {
    return lastValueFrom(this.http.delete<Event[]>(this.url + `/events/${id}`));
  }

  async getRecurring(orgId: string): Promise<RecurringEvent[]> {
    return lastValueFrom(
      this.http.get<RecurringEvent[]>(this.url + '/events/recurring', {
        headers: { 'selected-organization': orgId }
      })
    );
  }
}
